import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { wmsItemGroupService } from "services";
import { WmsItemGroup } from "types";
import SnackbarUtils from "utils/SnackbarUtils";

type WmsItemGroupListKey = ["wms-item-groups"];
type WmsItemGroupDetailKey = ["wms-item-groups", "detail", number | string];

const wmsItemGroupKeys = {
  all: ["wms-item-groups"] as const,
  list: (): WmsItemGroupListKey => ["wms-item-groups"],
  detail: (id: number | string): WmsItemGroupDetailKey => [
    "wms-item-groups",
    "detail",
    id,
  ],
};

export function useWmsItemGroup() {
  const queryClient = useQueryClient();

  const fetchList = (enabled = true) => {
    return useQuery({
      queryKey: wmsItemGroupKeys.list(),
      queryFn: () => wmsItemGroupService.list(),
      enabled,
      select: (data) => data ?? [],
      staleTime: 5 * 60 * 1000,
    });
  };

  const fetchDetail = (id: number | string, enabled = true) =>
    useQuery({
      queryKey: wmsItemGroupKeys.detail(id),
      queryFn: async () => {
        const list = await wmsItemGroupService.list();
        return list.find((item) => String(item.id) === String(id)) ?? null;
      },
      enabled: Boolean(id) && enabled,
    });

  const createGroup = useMutation({
    mutationFn: (payload: WmsItemGroup) => wmsItemGroupService.create(payload),
    onSuccess: (res) => {
      SnackbarUtils.success(res?.message ?? "Tạo nhóm hàng hóa thành công");
      queryClient.invalidateQueries({ queryKey: wmsItemGroupKeys.all });
      return res;
    },
    onError: (err: any) => {
      // Extract backend validation message if 422
      if (err?.response?.status === 422) {
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Dữ liệu không hợp lệ. Vui lòng kiểm tra lại.";
        SnackbarUtils.error(message);
      } else {
        SnackbarUtils.error("Tạo nhóm hàng hóa thất bại");
      }
      console.error("createWmsItemGroup failed", err);
    },
  });

  const updateGroup = useMutation({
    mutationFn: (payload: WmsItemGroup & { id: number | string }) =>
      wmsItemGroupService.update(payload.id, payload),
    onSuccess: (res) => {
      SnackbarUtils.success(
        res?.message ?? "Cập nhật nhóm hàng hóa thành công",
      );
      queryClient.invalidateQueries({ queryKey: wmsItemGroupKeys.all });
      return res;
    },
    onError: (err: any) => {
      // Extract backend validation message if 422
      if (err?.response?.status === 422) {
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Dữ liệu không hợp lệ. Vui lòng kiểm tra lại.";
        SnackbarUtils.error(message);
      } else {
        SnackbarUtils.error("Cập nhật nhóm hàng hóa thất bại");
      }
      console.error("updateWmsItemGroup failed", err);
    },
  });

  const deleteGroup = useMutation({
    mutationFn: (id: number | string) => wmsItemGroupService.destroy(id),
    onSuccess: (res) => {
      SnackbarUtils.success(res?.message ?? "Xóa nhóm hàng hóa thành công");
      queryClient.invalidateQueries({ queryKey: wmsItemGroupKeys.all });
      return res;
    },
    onError: (err: any) => {
      // Extract backend validation message if 422
      if (err?.response?.status === 422) {
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Dữ liệu không hợp lệ. Vui lòng kiểm tra lại.";
        SnackbarUtils.error(message);
      } else {
        SnackbarUtils.error("Xóa nhóm hàng hóa thất bại");
      }
      console.error("deleteWmsItemGroup failed", err);
    },
  });

  return {
    fetchList,
    fetchDetail,
    createGroup,
    updateGroup,
    deleteGroup,
  };
}
